/**
 * 根据二进制文件数据嗅探图片文件的类型
 * 目前仅关注 JPG/JPEG、PNG、GIF
 * @see https://github.com/sindresorhus/file-type/blob/main/core.js
 * @see https://developer.mozilla.org/zh-CN/docs/Web/HTTP/Guides/MIME_types#mime_%E5%97%85%E6%8E%A2
 * @see https://www.freeformatter.com/mime-types-list.html#mime-types-list
 * @param bytes 二进制文件
 */
export const sniffImageMimeByBinary = (
    bytes: Uint8Array
): { mime: string | null; ext: string | null } => {
    if (!bytes || bytes.length < 8) {
        return { mime: null, ext: null };
    }
    const isBytesMatch = (arr: number[], offset = 0) =>
        arr.every((b, i) => bytes[offset + i] === b);
    /**
     * JPEG/JPG 两个格式实际上是一样的，无法通过 Magic number 区分
     */
    if (isBytesMatch([0xff, 0xd8, 0xff])) {
        /**
         * 注意，理论上 image/jpg 是非标准的，但不是完全不可识别
         * @see https://stackoverflow.com/questions/33692835/is-the-mime-type-image-jpg-the-same-as-image-jpeg
         */
        return { mime: 'image/jpeg', ext: 'jpg' };
    }
    // PNG
    if (isBytesMatch([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
        return { mime: 'image/png', ext: 'png' };
    }
    // GIF87a / GIF89a
    if (
        isBytesMatch([0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
        isBytesMatch([0x47, 0x49, 0x46, 0x38, 0x39, 0x61])
    ) {
        return { mime: 'image/gif', ext: 'gif' };
    }

    // 不支持的格式
    return { mime: null, ext: null };
};

/**
 * 处理图片文件名
 * @param originalName 原始文件名（包含扩展名）
 * @param sniffedExt 嗅探出的扩展名
 */
export const getImageFileName = (originalName: string, sniffedExt: string | null) => {
    if (sniffedExt) {
        /**
         * 处理 JPEG 格式的双扩展名问题（.jpg 和 .jpeg）
         * 兼容老逻辑，防止出现由 xxxxxx.jpeg 得到 xxxxxx.jpeg.jpg 文件名的情况
         */
        const hasJpegExt = originalName.endsWith('.jpg') || originalName.endsWith('.jpeg');
        const isJpegFormat = sniffedExt === 'jpg';

        // 原始文件名是 .jpeg 或者 .jpg 后缀，且嗅探出来确实是 jpg，则直接保持原名
        if (isJpegFormat && hasJpegExt) {
            return originalName;
        }
        // 没有对应扩展名则添加
        if (!originalName.endsWith(`.${sniffedExt}`)) {
            const fileName = originalName.split('.').shift();
            return `${fileName}.${sniffedExt}`;
        }
    }
    // 没有嗅探出来扩展名则保持原名
    return originalName;
};

/**
 * 处理图片名称，确保图片名称有正确的后缀
 * @param serverId 服务器返回的图片ID或路径
 * @param mimeType 图片的MIME类型
 * @returns 处理后的图片名称
 */
export const processImageName = (serverId: string, mimeType: string) => {
    const extensionMap: Record<string, string> = {
        'image/jpeg': '.jpeg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif'
    };

    const extension = extensionMap[mimeType] || '.jpg';
    // 检查是否已经有图片扩展名
    const hasImageExtension = /\.(jpg|jpeg|png|gif)$/i.test(serverId);

    return hasImageExtension ? serverId : `${serverId}${extension}`;
};
